// src/Studio/timeline/fxRuntime.packets.ts
//
// Task T8 of the 2026-04-08 studio file-splits plan. Pure mechanical
// extraction from fxRuntime.ts — no behavior change.
//
// Owns:
//   - computeFxRenderPackets (the per-frame packet coordinator)
//
// This module imports types-only from fxRuntime.ts (via import type) so
// there is no runtime circular import — the re-export in fxRuntime.ts
// points here but this file never imports runtime values from core.

import type { Clip, Track } from "./EditorStore";
import type { FxBlock } from "./fxTypes";
import type { FxRenderPacket, FxQualityTier } from "./fxRuntime";
import { sortFxBlocksByTrackOrder } from "./fxTypes";
import { resolveFxRenderPath, resolveFxGpuModuleId } from "./fxEffectCatalog";
import {
  logFxBlockActivityTransition,
  cleanupStaleActivityEntries,
} from "./fxRuntime.activity";
import {
  dedupeFxBlocksById,
  getFxClipsByIdCached,
  getTargetElementIds,
  isPlayheadWithinFxWindow,
} from "./fxRuntime.targeting";
import {
  evaluateFxBlockAtPlayhead,
  cleanupStaleModulationState,
  resolveFxBlockCostMs,
} from "./fxRuntime.evaluation";
import { clamp01 } from "./fxRuntime.shared";

export function computeFxRenderPackets(args: {
  playhead: number;
  clips: Clip[];
  tracks: Track[];
  fxBlocks: FxBlock[];
  qualityTier?: FxQualityTier;
}): Map<string, FxRenderPacket[]> {
  const { playhead, clips, tracks, fxBlocks, qualityTier = "balanced" } = args;
  const byElementId = new Map<string, FxRenderPacket[]>();
  const seenBlockIds = new Set<string>();
  const uniqueFxBlocks = dedupeFxBlocksById(fxBlocks);

  for (const block of uniqueFxBlocks) {
    seenBlockIds.add(block.id);
    logFxBlockActivityTransition(
      block,
      playhead,
      block.enabled &&
        isPlayheadWithinFxWindow(playhead, block.start, block.end),
    );
  }
  cleanupStaleActivityEntries(seenBlockIds);
  cleanupStaleModulationState(seenBlockIds);

  const activeBlocks = uniqueFxBlocks.filter(
    (block) =>
      block.enabled &&
      isPlayheadWithinFxWindow(playhead, block.start, block.end),
  );
  if (activeBlocks.length === 0) return byElementId;

  const trackOrderById = new Map<string, number>(
    tracks.map((t) => [t.id, t.order ?? 0]),
  );
  const orderedBlocks = sortFxBlocksByTrackOrder(activeBlocks, trackOrderById);
  // Build clip lookup once per frame, shared across all blocks
  const clipById = getFxClipsByIdCached(clips);

  for (const block of orderedBlocks) {
    const targets = getTargetElementIds(block, clips, playhead, clipById);
    if (targets.size === 0) continue;

    const duration = Math.max(0.001, block.end - block.start);
    const progress = clamp01((playhead - block.start) / duration);
    const evaluated = evaluateFxBlockAtPlayhead(block, playhead);
    const packet: FxRenderPacket = {
      fxBlockId: block.id,
      effectType: block.effectType,
      renderPath: resolveFxRenderPath(block.effectType),
      category: block.category,
      blendMode: block.blendMode,
      opacity: evaluated.opacity,
      mask: evaluated.mask,
      layers: evaluated.layers,
      seed: block.seed,
      progress,
      qualityTier,
      modulation: evaluated.modulation,
      params: evaluated.params,
      durationSec: duration,
      gpuModuleId: resolveFxGpuModuleId(block.effectType),
    };

    for (const elementId of targets) {
      const existing = byElementId.get(elementId);
      if (existing) {
        existing.push(packet);
      } else {
        byElementId.set(elementId, [packet]);
      }
    }
  }

  // Enforce per-element FX budget — drop lowest-priority blocks that exceed 8ms
  for (const [elementId, packets] of byElementId.entries()) {
    if (packets.length <= 1) continue;
    const { kept } = enforceFxBudget(packets);
    if (kept.length < packets.length) {
      byElementId.set(elementId, kept);
    }
  }

  return byElementId;
}

// ── FX budget enforcement ───────────────────────────────────────

const FX_BUDGET_MS = 8;

/**
 * Enforce a per-frame FX budget per element. Keeps packets in order
 * (highest priority first, as determined by track/stack order).
 * Drops lowest-priority packets (last in the list) when cumulative
 * estimated cost exceeds the budget.
 */
export function enforceFxBudget(
  packets: FxRenderPacket[],
  budgetMs: number = FX_BUDGET_MS,
): { kept: FxRenderPacket[]; dropped: number } {
  let remaining = budgetMs;
  const kept: FxRenderPacket[] = [];
  let dropped = 0;
  for (const packet of packets) {
    const cost = resolveFxBlockCostMs(packet.renderPath, 1);
    if (remaining >= cost) {
      remaining -= cost;
      kept.push(packet);
    } else {
      dropped++;
    }
  }
  return { kept, dropped };
}
